import './App.css';
import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from './i18n';
import SEO from './components/SEO';

const CONTACT_EMAIL = 'contact@tapiotulenheimo.fi';
const LINKEDIN_URL = 'https://www.linkedin.com/in/tapio-tulenheimo-629875156';

function App() {
  const { t, lang, basePath } = useTranslation();
  const [openPopup, setOpenPopup] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const closePopup = useCallback(() => setOpenPopup(null), []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpenPopup(null);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    const isOpen = Boolean(openPopup);
    if (!isOpen) return;

    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    return () => {
      const y = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(y || '0', 10) * -1);
    };
  }, [openPopup]);

  const homeHref = basePath ? `${basePath}#home` : '#home';
  const contactHref = basePath ? `${basePath}#contact` : '#contact';

  const ariaLabelDialog =
    openPopup === 'media'
      ? t.media.label
      : openPopup === 'profileMore'
        ? t.popup.about.title
        : openPopup;

  return (
    <div className="App">
      <SEO t={t} lang={lang} />
      <nav className={'navbar' + (scrolled ? ' scrolled' : '')}>
        <div className="navbar-container">
          <a href={homeHref} className="logo">
            {t.nav.logo}
          </a>
          <div className="navbar-right">
            <a href={contactHref} className="nav-contact">
              {t.nav.contact}
            </a>
            <span className="lang-switcher">
              <Link
                to="/"
                className={lang === 'fi' ? 'lang-active' : 'lang-link'}
                aria-current={lang === 'fi' ? 'page' : undefined}
              >
                FI
              </Link>
              <span className="lang-sep">|</span>
              <Link
                to="/en/"
                className={lang === 'en' ? 'lang-active' : 'lang-link'}
                aria-current={lang === 'en' ? 'page' : undefined}
              >
                EN
              </Link>
            </span>
          </div>
        </div>
      </nav>

      <header id="home" className="hero-section">
        <div className="hero-image" aria-hidden="true" />
        <div className="hero-text">
          <h1 className="hero-intro">
            {t.hero.line1}{' '}
            <span className="hero-break">{t.hero.line2}</span>
          </h1>
        </div>
      </header>

      <section id="profile" className="profile-section">
        <div className="profile-image-block">
          <picture>
            <source
              srcSet="/images/tapio_tulenheimo_compressed.avif"
              type="image/avif"
            />
            <img
              src="/images/profile-2400w.jpg"
              width={720}
              height={562}
              alt="Tapio Tulenheimo"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </picture>
        </div>

        <div className="profile-text-block">
          <h2 className="profile-title">{t.profile.title}</h2>

          <p className="profile-body">{t.profile.para1}</p>

          <p className="profile-body">{t.profile.para2}</p>

          <p className="profile-body">{t.profile.para3}</p>

          <button
            type="button"
            className="profile-readmore-btn"
            onClick={() => setOpenPopup('profileMore')}
          >
            {t.profile.readMore}
            <span className="link-arrow"> →</span>
          </button>

          <div className="profile-credentials">
            <p className="profile-credentials-text">
              {t.profile.credentials.split('\n').map((line, i) => (
                <span key={i}>
                  {i > 0 && <br />}
                  {line}
                </span>
              ))}
            </p>
          </div>

          <div className="profile-themes">
            <button
              type="button"
              className="profile-theme-btn"
              onClick={() => setOpenPopup('coaching')}
            >
              {t.profile.coaching}
              <span className="link-arrow"> →</span>
            </button>
            <button
              type="button"
              className="profile-theme-btn"
              onClick={() => setOpenPopup('speaking')}
            >
              {t.profile.speaking}
              <span className="link-arrow"> →</span>
            </button>
          </div>
        </div>
      </section>

      <section id="media" className="media-section">
        <div className="media-content">
          <button
            className="media-label-btn"
            type="button"
            aria-haspopup="dialog"
            onClick={() => setOpenPopup('media')}
          >
            <span className="media-label">
              {t.media.label}
              <span className="link-arrow"> →</span>
            </span>
          </button>
        </div>
      </section>

      <section id="contact" className="contact-section">
        <h2 className="contact-heading">{t.contact.heading}</h2>
        <a href={`mailto:${CONTACT_EMAIL}`} className="contact-link">
          {CONTACT_EMAIL}
        </a>
        <a
          className="contact-linkedin"
          href={LINKEDIN_URL}
          target="_blank"
          rel="noreferrer"
        >
          <span className="contact-linkedin-text">{t.contact.linkedIn}</span>
          <span className="contact-linkedin-arrow" aria-hidden="true">
            →
          </span>
        </a>
      </section>

      <footer className="footer">
        <p>
          {t.footer.copyright.replace('{year}', String(new Date().getFullYear()))}
        </p>
      </footer>

      {openPopup && (
        <div
          className="editorial-overlay"
          onClick={closePopup}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabelDialog}
        >
          <div className="editorial-note" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="editorial-close"
              onClick={closePopup}
              aria-label={t.popup.close}
            >
              ×
            </button>
            {openPopup === 'coaching' && (
              <div className="editorial-content">
                <div className="editorial-title">
                  {t.popup.coaching.title}
                </div>
                <p className="editorial-body">{t.popup.coaching.para1}</p>
                <p className="editorial-body">{t.popup.coaching.para2}</p>
                <p className="editorial-contact">
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                </p>
              </div>
            )}

            {openPopup === 'speaking' && (
              <div className="editorial-content">
                <div className="editorial-title">
                  {t.popup.speaking.title}
                </div>
                <p className="editorial-body">{t.popup.speaking.para1}</p>
                <p className="editorial-body">{t.popup.speaking.para2}</p>
                <p className="editorial-contact">
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                </p>
              </div>
            )}

            {openPopup === 'profileMore' && (
              <div className="editorial-content">
                <div className="editorial-title">{t.popup.about.title}</div>
                <p className="editorial-body">{t.popup.about.para1}</p>
                <p className="editorial-body">{t.popup.about.para2}</p>
                <p className="editorial-body">{t.popup.about.para3}</p>
                <p className="editorial-body">{t.popup.about.para4}</p>
                <p className="editorial-body">{t.popup.about.para5}</p>
              </div>
            )}

            {openPopup === 'media' && (
              <div className="editorial-content editorial-media">
                <div className="editorial-title">{t.popup.media.title}</div>
                <p className="editorial-body">{t.popup.media.intro1}</p>
                <p className="editorial-body">{t.popup.media.intro2}</p>
                <ul className="editorial-media-list media-list">
                  <li>
                    <a
                      className="media-item"
                      href={t.popup.media.companyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="media-title">
                        {t.popup.media.companyLabel}
                      </span>
                      <span className="media-arrow"> →</span>
                    </a>
                  </li>
                  {t.popup.media.links.map((item, i) => (
                    <li key={i}>
                      <a
                        className="media-item"
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="media-title">{item.name}</span>
                        <span className="media-arrow"> →</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
